import { useEffect, useState } from 'react';
import Image from 'next/image';
import cn from 'classnames';
import styled from 'styled-components';

import { clientConfig } from '@/config/clientConfig';
import { useNotices } from '@/features/notices/useNotices';
import { makeEntities } from '@/features/smartImport/helpers';
import { JobStatus } from '@/features/smartImport/types';
import { COLORS } from '@/shared/constants/colors';
import { useSJDatabase } from '@/shared/lib/sj-orm';
import { BFC } from '@/shared/types';
import { Title } from '@/shared/uikit/Title';
import { AiButton } from '@/shared/uikitV2/AiButton';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { useAiExtraction } from '@/widgets/ExtractFormData/hooks/useAiExtraction';

import { useModalsStore } from '../modals';

import { CircleProgress } from './components/CircleProgress';
import { isExtractionTypeEntity } from './helpers';
import { EntitiesToExtract, EntitiesToExtractName } from './types';

type ExtractFormDataProps = {
  setExtractedData: (entity: EntitiesToExtract) => void;
  addFiles?: (filesItemInfos: File[]) => void;
  subtype: string;
  entityType: EntitiesToExtractName;
};

export const ExtractFormData: BFC<ExtractFormDataProps> = ({
  setExtractedData,
  addFiles,
  entityType,
  subtype,
  style,
}) => {
  const { t } = useTranslation('widgets/extractFormData');
  const modals = useModalsStore();
  const [step, setStep] = useState<'step1' | 'step2'>('step1');
  const notices = useNotices();

  const { extractFiles, jobId, stopPolling, runJob, jobDoc, resetStates, fetchPendingJob } =
    useAiExtraction();
  const sjdb = useSJDatabase();

  const handleModal = () => {
    modals.openModal({
      type: 'ExtractModal',
      entityType,
      extractFiles,
      addFiles,
      subtype,
    });
  };
  useEffect(() => {
    if (clientConfig.isExported) {
      return undefined;
    }
    if (!sjdb.isDbReady) return undefined;

    if (jobId) {
      runJob(jobId, () => {
        setStep('step1');
      });

      setStep('step2');
    } else {
      fetchPendingJob();
    }

    return () => stopPolling();
  }, [sjdb.isDbReady, jobId]);

  useEffect(() => {
    if (jobDoc?.status !== JobStatus.DONE) return;
    stopPolling();

    const createAndShow = async () => {
      if (!jobDoc?.results) return;
      const items = jobDoc.results.flatMap((result) => result.results);
      const entities = makeEntities(items, {});

      const currentEntity = entities[entityType][0];

      if (
        currentEntity !== undefined &&
        currentEntity !== null &&
        typeof currentEntity === 'object' &&
        isExtractionTypeEntity(currentEntity)
      ) {
        setExtractedData(currentEntity);
      } else {
        notices.error(t('notices.error'));
      }

      resetStates();
      setStep('step1');
    };

    createAndShow();
  }, [jobDoc?.status, entityType, setExtractedData]);

  return (
    <Container className={cn(step)} style={style}>
      <div className={cn('step-content', { active: step === 'step1' })}>
        <Image
          className="image"
          alt=""
          src="/assets/widgets/ExtractToForm/extract-to-form-step1.png"
          width={165}
          height={65}
        />
        <Title className="middle" size={16} bold>
          {t.rich('step1.title')}
        </Title>
        <AiButton onClick={handleModal} style={{ height: 40 }}>
          {t('uploadBtn')}
        </AiButton>
      </div>

      <div className={cn('step-content', { active: step === 'step2' })}>
        <Image
          className="image"
          alt=""
          src="/assets/widgets/ExtractToForm/extract-to-form-step2.png"
          width={165}
          height={65}
        />
        <div className="middle">
          <Title size={14} bold>
            {t('step2.title')}
          </Title>
          <div className="files-count">
            {`${jobDoc?.done ?? 0}/${jobDoc?.total ?? 0} `}
            {t('step2.items')}
          </div>
        </div>
        {step === 'step2' && <CircleProgress />}
      </div>
    </Container>
  );
};

const Container = styled.div`
  border-radius: 8px;
  position: relative;
  overflow: hidden;
  height: 64px;
  transition: background-color 0.3s ease;

  &.step1 {
    background-color: ${COLORS.systemLight200};
  }

  &.step2 {
    background-color: ${COLORS.systemSky100};
  }

  .step-content {
    position: absolute;
    inset: 0;
    padding: 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    opacity: 0;
    transform: translateY(8px);
    pointer-events: none;
    transition:
      opacity 0.3s ease,
      transform 0.3s ease;
  }

  .step-content.active {
    opacity: 1;
    transform: translateY(0);
    pointer-events: auto;
  }

  .image {
    position: absolute;
    bottom: 0px;
    left: 0;
  }

  .middle {
    margin-left: 150px;
  }

  .files-count {
    font-size: 14px;
    color: ${COLORS.systemDark200};
  }
`;
